import type {
  FlagImpactResult,
  OwnerFlagHistoryEntry,
} from '../../../services/ownerConsoleService'

export const RAMP_PRESETS: readonly number[] = [10, 25, 50, 100]

export interface FormattedFlagImpact {
  scannedText: string
  turnsOn: number
  turnsOff: number
  dependents: Array<{
    key: string
    turnsOn: number
    turnsOff: number
  }>
}

export function formatImpact(result: FlagImpactResult): FormattedFlagImpact {
  const scannedText = result.capped
    ? `${result.scanned > 0 ? result.scanned : 5000}+`
    : String(result.scanned)
  return {
    scannedText,
    turnsOn: result.target?.turnsOn ?? 0,
    turnsOff: result.target?.turnsOff ?? 0,
    dependents: (result.dependents || []).map((dep) => ({
      key: dep.key,
      turnsOn: dep.turnsOn,
      turnsOff: dep.turnsOff,
    })),
  }
}

export interface FlagHistoryChangeParts {
  defaultChanged?: { from: boolean; to: boolean }
  rulesCountChanged?: { from: number; to: number }
  dependenciesAdded: string[]
  dependenciesRemoved: string[]
  killed: boolean
  restored: boolean
  rolledBackFrom: number | null
}

export function describeFlagHistoryChange(
  entry: OwnerFlagHistoryEntry,
  olderEntry: OwnerFlagHistoryEntry | null,
): FlagHistoryChangeParts {
  const killed = entry.action === 'kill' || (entry.killed && !olderEntry?.killed)
  const restored = entry.action === 'restore' || (!entry.killed && Boolean(olderEntry?.killed))
  const rolledBackFrom = entry.rolledBackFrom ?? (entry.action === 'rollback' && olderEntry ? olderEntry.version : null)

  let defaultChanged: { from: boolean; to: boolean } | undefined
  let rulesCountChanged: { from: number; to: number } | undefined
  const dependenciesAdded: string[] = []
  const dependenciesRemoved: string[] = []

  if (olderEntry) {
    if (entry.defaultValue !== olderEntry.defaultValue) {
      defaultChanged = { from: olderEntry.defaultValue, to: entry.defaultValue }
    }
    if ((entry.rules?.length ?? 0) !== (olderEntry.rules?.length ?? 0)) {
      rulesCountChanged = {
        from: olderEntry.rules?.length ?? 0,
        to: entry.rules?.length ?? 0,
      }
    }
    const oldDeps = olderEntry.dependsOn || []
    const newDeps = entry.dependsOn || []
    for (const dep of newDeps) {
      if (!oldDeps.includes(dep)) {
        dependenciesAdded.push(dep)
      }
    }
    for (const dep of oldDeps) {
      if (!newDeps.includes(dep)) {
        dependenciesRemoved.push(dep)
      }
    }
  }

  return {
    defaultChanged,
    rulesCountChanged,
    dependenciesAdded,
    dependenciesRemoved,
    killed,
    restored,
    rolledBackFrom,
  }
}

export const FLAG_HISTORY_ACTION_NLS_KEYS = {
  update: 'NLSOwnerConsoleFlagsHistoryActionUpdate',
  kill: 'NLSOwnerConsoleFlagsHistoryActionKill',
  restore: 'NLSOwnerConsoleFlagsHistoryActionRestore',
  rollback: 'NLSOwnerConsoleFlagsHistoryActionRollback',
} as const

const SPECIFIC_FLAG_ERROR_CODES = new Set([
  'invalid-argument',
  'failed-precondition',
  'not-found',
])

export function extractCallableErrorCode(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const raw = String((error as { code: unknown }).code)
    return raw.replace(/^functions\//, '')
  }
  return ''
}

export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }
  return String(error)
}

export function getFlagMutationErrorKeyAndParams(error: unknown): {
  key: 'NLSOwnerConsoleFlagsErrorAction' | 'NLSOwnerConsoleFlagsErrorSave'
  params?: { message: string }
} {
  const code = extractCallableErrorCode(error)
  if (SPECIFIC_FLAG_ERROR_CODES.has(code)) {
    return {
      key: 'NLSOwnerConsoleFlagsErrorAction',
      params: { message: extractErrorMessage(error) },
    }
  }
  return {
    key: 'NLSOwnerConsoleFlagsErrorSave',
  }
}
